/**
 * Navigation Configuration
 * Role-based menu items with paths and icons
 */

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon?: string; // icon key from icons.tsx
  /** Static badge value — used when the count doesn't come from a live source. */
  badge?: number;
  /**
   * Pull the badge from a top-level field of the call-center tickets summary.
   * Currently `'open'` is used for the "All Tickets" item.
   */
  badgeKey?: 'open' | 'high_priority' | 'overdue' | 'total';
  /**
   * Pull the badge from `summary.by_category[<this>]`. Values mirror the
   * backend's TicketCategory enum.
   */
  badgeCategory?: 'customer' | 'delivery' | 'payment' | 'inventory' | 'compliance' | 'returns' | 'other';
  section?: string; // section header
}

export interface NavSection {
  section?: string;
  items: NavItem[];
}

// Admin navigation (full access to all features)
export const ADMIN_NAV: NavSection[] = [
  {
    section: 'Main',
    items: [
      { id: 'overview', label: 'Overview', path: '/dashboard', icon: 'OverviewIcon' },
      { id: 'ai-insights', label: 'AI Insights', path: '/ai-insights', icon: 'AiIcon' },
    ],
  },
  {
    section: 'Management',
    items: [
      { id: 'users', label: 'Users', path: '/users', icon: 'CustomersIcon' },
      { id: 'customers', label: 'Customers', path: '/customers', icon: 'CustomersIcon' },
      { id: 'inventory', label: 'Inventory', path: '/inventory', icon: 'InventoryIcon' },
      { id: 'routes', label: 'Routes', path: '/routes', icon: 'RoutesIcon' },
      { id: 'route-density', label: 'Route Density', path: '/route-density', icon: 'DensityIcon' },
      { id: 'store-tiering', label: 'Store Tiering', path: '/store-tiering', icon: 'TieringIcon' },
      { id: 'sales', label: 'Sales', path: '/sales', icon: 'SalesIcon' },
      { id: 'categories', label: 'Categories', path: '/categories', icon: 'CategoriesIcon' },
      { id: 'orders', label: 'Orders', path: '/orders', icon: 'OrdersIcon' },
      { id: 'product-catalog', label: 'Product Catalog', path: '/product-catalog', icon: 'CatalogIcon' },
      { id: 'purchase-orders', label: 'Purchase Orders', path: '/purchase-orders', icon: 'PurchaseIcon' },
      { id: 'stock-management', label: 'Stock Management', path: '/stock-management', icon: 'StockIcon' },
      { id: 'promotions', label: 'Promotions', path: '/promotions', icon: 'PromotionsIcon' },
    ],
  },
  {
    section: 'Finance',
    items: [
      { id: 'tax-compliance', label: 'Tax Compliance', path: '/tax-compliance', icon: 'ComplianceIcon' },
      { id: 'accounting', label: 'Accounting', path: '/accounting', icon: 'AccountingIcon' },
      { id: 'invoices', label: 'Invoices', path: '/invoices', icon: 'InvoicesIcon' },
      { id: 'payments', label: 'Payments', path: '/payments', icon: 'PaymentsIcon' },
      { id: 'price-match-requests', label: 'Price Match Requests', path: '/price-match-requests', icon: 'PaymentsIcon' },
      { id: 'counter-sales-history', label: 'Counter Sales', path: '/counter-sales-history', icon: 'SalesIcon' },
    ],
  },
  {
    section: 'Operations',
    items: [
      { id: 'onboarding', label: 'Onboarding Applications', path: '/admin/onboarding', icon: 'OnboardingIcon', badge: 0 },
      { id: 'live-tracking', label: 'Live Tracking', path: '/live-tracking', icon: 'LiveTrackingIcon' },
    ],
  },
];

// Sales Rep navigation (sales focused)
export const SALES_REP_NAV: NavSection[] = [
  {
    items: [
      { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'DashboardIcon' },
      { id: 'customers', label: 'Customers', path: '/customers', icon: 'CustomersIcon' },
      { id: 'orders', label: 'Orders', path: '/orders', icon: 'OrdersIcon' },
      { id: 'activities', label: 'Activities', path: '/activities', icon: 'RoutesIcon' },
      { id: 'price-match-requests', label: 'Price Match Requests', path: '/price-match-requests', icon: 'PaymentsIcon' },
      { id: 'goals', label: 'Goals & Targets', path: '/goals-targets', icon: 'TieringIcon' },
      { id: 'ai-insights', label: 'AI Insights', path: '/ai-insights', icon: 'AiIcon' },
      { id: 'profile', label: 'Profile & Settings', path: '/profile', icon: 'CustomersIcon' },
    ],
  },
];

// Driver navigation (delivery focused)
export const DRIVER_NAV: NavSection[] = [
  {
    items: [
      { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'DashboardIcon' },
      { id: 'my-routes', label: 'My Routes', path: '/routes', icon: 'RoutesIcon' },
      { id: 'active-deliveries', label: 'Active Deliveries', path: '/active-deliveries', icon: 'LiveTrackingIcon' },
      { id: 'collections', label: 'Collections', path: '/collections', icon: 'PromotionsIcon' },
      { id: 'failed-deliveries', label: 'Failed Deliveries', path: '/failed-deliveries', icon: 'InvoicesIcon' },
      { id: 'history', label: 'History', path: '/history', icon: 'SalesIcon' },
      { id: 'returns', label: 'Returns', path: '/returns', icon: 'OrdersIcon' },
      { id: 'performance', label: 'Performance', path: '/performance', icon: 'DensityIcon' },
      { id: 'profile', label: 'Profile & Settings', path: '/profile', icon: 'CustomersIcon' },
    ],
  },
  {
    section: 'Quick Actions',
    items: [
      { id: 'view-route-map', label: 'View Route Map', path: '/routes', icon: 'RouteMapIcon' },
      { id: 'call-dispatcher', label: 'Call Dispatcher', path: '#dispatcher', icon: 'SupportIcon' },
      { id: 'report-issue', label: 'Report Vehicle Issue', path: '#report', icon: 'SearchIcon' },
    ],
  },
];

// Warehouse Manager navigation (inventory focused)
export const WAREHOUSE_MANAGER_NAV: NavSection[] = [
  {
    items: [
      { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'DashboardIcon' },
      { id: 'warehouse-scanner', label: 'Warehouse Scanner', path: '/warehouse-scanner', icon: 'SearchIcon' },
      { id: 'counter-sales', label: 'Counter Sales (POS)', path: '/counter-sales', icon: 'SalesIcon' },
      { id: 'counter-sales-history', label: 'Counter Sales', path: '/counter-sales-history', icon: 'OrdersIcon' },
      { id: 'pick-pack', label: 'Pick & Pack', path: '/pick-pack', icon: 'OrdersIcon' },
      { id: 'receiving', label: 'Receiving', path: '/receiving', icon: 'InventoryIcon' },
      { id: 'inventory', label: 'Inventory', path: '/inventory', icon: 'StockIcon' },
      { id: 'transfers', label: 'Transfers', path: '/transfers', icon: 'RoutesIcon' },
      { id: 'returns', label: 'Returns', path: '/returns', icon: 'DensityIcon' },
      { id: 'reports', label: 'Reports', path: '/reports', icon: 'CatalogIcon' },
    ],
  },
  {
    section: 'Quick Actions',
    items: [
      { id: 'scan-products', label: 'Scan Products', path: '/scan-products', icon: 'SearchIcon' },
      { id: 'receive-shipment', label: 'Receive shipment', path: '#receive', icon: 'InvoicesIcon' },
      { id: 'report-inventory', label: 'Report Inventory Issue', path: '#inventory-issue', icon: 'SalesIcon' },
    ],
  },
];

// Call Center navigation (customer service focused).
// Badge counts are filled in at render time by Sidebar.tsx from
// GET /api/v1/tickets/summary (open count + by_category buckets).
// The category strings on each item match the backend's `TicketCategory`
// enum so Sidebar can index `summary.by_category[badgeCategory]` directly.
export const CALL_CENTER_NAV: NavSection[] = [
  {
    section: 'Main',
    items: [
      { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'DashboardIcon' },
      { id: 'all-tickets', label: 'All Tickets', path: '/support', icon: 'InvoicesIcon', badgeKey: 'open' },
    ],
  },
  {
    section: 'By Module',
    items: [
      { id: 'customer-issues',   label: 'Customer Issues',   path: '/customer-issues',   icon: 'CustomersIcon',  badgeCategory: 'customer'   },
      { id: 'delivery-issues',   label: 'Delivery Issues',   path: '/delivery-issues',   icon: 'RoutesIcon',     badgeCategory: 'delivery'   },
      { id: 'payment-issues',    label: 'Payment Issues',    path: '/payment-issues',    icon: 'PaymentsIcon',   badgeCategory: 'payment'    },
      { id: 'inventory-issues',  label: 'Inventory Issues',  path: '/inventory-issues',  icon: 'InventoryIcon',  badgeCategory: 'inventory'  },
      { id: 'compliance-issues', label: 'Compliance Issues', path: '/compliance-issues', icon: 'ComplianceIcon', badgeCategory: 'compliance' },
      { id: 'returns-disputes',  label: 'Returns & Disputes', path: '/returns-disputes', icon: 'OrdersIcon',     badgeCategory: 'returns'    },
    ],
  },
  {
    section: 'Resources',
    items: [
      { id: 'knowledge-base', label: 'Knowledge Base', path: '/knowledge-base', icon: 'CatalogIcon' },
      { id: 'profile', label: 'Profile & Settings', path: '/profile', icon: 'CustomersIcon' },
    ],
  },
];

// Customer navigation (customer portal)
export const CUSTOMER_NAV: NavSection[] = [
  {
    items: [
      { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'DashboardIcon' },
      { id: 'catalog', label: 'Catalog', path: '/catalog', icon: 'CatalogIcon' },
      { id: 'orders', label: 'Orders', path: '/customer-orders', icon: 'OrdersIcon' },
      { id: 'deliveries', label: 'Deliveries', path: '/customer-deliveries', icon: 'RoutesIcon' },
      { id: 'invoices-payment', label: 'Invoices & Payment', path: '/customer-invoices', icon: 'InvoicesIcon' },
      { id: 'notifications', label: 'Notifications', path: '/notifications', icon: 'OnboardingIcon' },
      { id: 'profile', label: 'Profile & Settings', path: '/profile', icon: 'CustomersIcon' },
    ],
  },
];

// Default navigation (basic access)
export const DEFAULT_NAV: NavSection[] = [
  {
    section: 'Main',
    items: [
      { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'DashboardIcon' },
    ],
  },
];

/**
 * Get navigation config by role
 * Aligned with backend roles: Admin, Sales Rep, Driver, Warehouse Manager, Call Center, Customer
 */
export function getNavByRole(role: string): NavSection[] {
  const normalizedRole = (role || 'default').toLowerCase().trim();

  switch (normalizedRole) {
    case 'admin':
      return ADMIN_NAV;
    case 'sales rep':
    case 'sales_rep':
      return SALES_REP_NAV;
    case 'driver':
      return DRIVER_NAV;
    case 'warehouse manager':
    case 'warehouse_manager':
      return WAREHOUSE_MANAGER_NAV;
    case 'call center':
    case 'call_center':
      return CALL_CENTER_NAV;
    case 'customer':
      return CUSTOMER_NAV;
    default:
      return DEFAULT_NAV;
  }
}

/**
 * Role subtitle mapping for sidebar header
 * Aligned with backend roles
 */
export const ROLE_SUBTITLES: Record<string, string> = {
  admin: 'Admin Panel',
  'sales rep': 'Sales Platform',
  'sales_rep': 'Sales Platform',
  driver: 'Driver Portal',
  'warehouse manager': 'Warehouse Management',
  'warehouse_manager': 'Warehouse Management',
  'call center': 'Call Center',
  'call_center': 'Call Center',
  customer: 'Customer Portal',
  default: 'Dashboard',
};

export function getRoleSubtitle(role: string): string {
  const normalizedRole = (role || 'default').toLowerCase();
  return ROLE_SUBTITLES[normalizedRole] || ROLE_SUBTITLES.default;
}
